import {
  CheckOutlined,
  LeftOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  notification,
  Row,
} from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useAddExistingTreuhaenderToKundensatzMutation,
  useAddExistingTreuhandstelleToKundensatzMutation,
  useLazyFindTreuhaenderQuery,
} from "store/masterdata";
import { FindTreuhaenderParams, Treuhaender } from "types/masterdata.types";
import DataTable from "components/elements/DataTable";
import { ColumnType } from "components/elements/DataTable/DataTable";
import ConfirmationModal from "views/App/components/Modals/ConfirmationModal";
import styles from "../ChangeTHsModal.module.less";
import CreateNewTHForm from "../Forms/CreateNewTHForm";

interface Props {
  visible: boolean;
  kundennummer: number;
  selectedTHs: number;
  onClose(): void;
  onSuccess(): void;
}

function SearchTH({
  visible,
  kundennummer,
  selectedTHs,
  onClose,
  onSuccess,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [selectedTH, setSelectedTH] = useState<number | null>(null);
  const [confirmationModalVisible, setConfirmationModalVisible] =
    useState(false);
  const [createNewTHFormModalVisible, setCreateNewTHFormModalVisible] =
    useState(false);

  const [findTreuhaender, { data, isLoading, isFetching }] =
    useLazyFindTreuhaenderQuery();
  const [addExistingTreuhaenderToKundensatzMutation, addExistingTHResult] =
    useAddExistingTreuhaenderToKundensatzMutation();
  const [addExistingTreuhandstelleToKundensatzMutation, addExistingTHsResult] =
    useAddExistingTreuhandstelleToKundensatzMutation();

  const onSearch = (values: FindTreuhaenderParams) => {
    setSelectedTH(null);
    findTreuhaender(values);
  };

  const onInitialSearch = ({
    treuhandstelleId,
  }: {
    treuhandstelleId: number;
  }) => {
    findTreuhaender({
      firstname: "",
      lastname: "",
      address: "",
      place: "",
      postalCode: "",
      treuhandstelleId,
    });
  };

  useEffect(() => {
    onInitialSearch({
      treuhandstelleId: selectedTHs,
    });
  }, [selectedTHs]);

  const showConfirmationModal = () => {
    setConfirmationModalVisible(true);
  };

  const onConfirm = async () => {
    if (selectedTH) {
      try {
        const addExistingTHsResponse =
          await addExistingTreuhandstelleToKundensatzMutation({
            kundennummer,
            treuhandstelleId: selectedTHs,
          }).unwrap();
        const addExistingTHResponse =
          await addExistingTreuhaenderToKundensatzMutation({
            kundennummer,
            treuhaenderId: selectedTH,
          }).unwrap();

        if (
          !addExistingTHResponse.hasErrors &&
          !addExistingTHsResponse.hasErrors
        ) {
          notification.success({
            message: t("masterdata.lw_details.add_ths_and_th_success"),
          });
          onClose();
          onSuccess();
        }
      } catch (error) {
        console.error("Error", error);
        onClose();
      }
    }
  };

  return (
    <Modal
      footer={null}
      title={t("masterdata.lw_details.search_th")}
      visible={visible}
      onCancel={onClose}
      width={1000}
      destroyOnClose
    >
      <Form
        id="landwirt-form"
        form={form}
        onFinish={onSearch}
        layout="vertical"
        initialValues={{
          firstname: "",
          lastname: "",
          address: "",
          place: "",
          postalCode: "",
          treuhandstelleId: selectedTHs,
        }}
      >
        <Row gutter={10}>
          <Col span={5}>
            <Form.Item name="firstname">
              <Input placeholder={t("masterdata.lw_details.firstname")} />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item name="lastname">
              <Input placeholder={t("masterdata.lw_details.lastname")} />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item name="address">
              <Input placeholder={t("masterdata.lw_details.address")} />
            </Form.Item>
          </Col>
          <Col span={3}>
            <Form.Item name="postalCode">
              <Input placeholder={t("masterdata.lw_details.postal_code")} />
            </Form.Item>
          </Col>
          <Col span={3}>
            <Form.Item name="place">
              <Input placeholder={t("masterdata.lw_details.ort")} />
            </Form.Item>
          </Col>
          <Col hidden span={3}>
            <Form.Item name="treuhandstelleId">
              <Input />
            </Form.Item>
          </Col>
          <Col span={3}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              htmlType="submit"
              block
            >
              {t("search")}
            </Button>
          </Col>
        </Row>
      </Form>
      <div className={styles.table_container}>
        <DataTable<Treuhaender>
          isLoading={isLoading || isFetching}
          actions={[]}
          headers={[
            {
              title: t("masterdata.lw_details.firstname"),
              key: "firstname",
              type: ColumnType.Text,
              width: 100,
            },
            {
              title: t("masterdata.lw_details.lastname"),
              key: "lastname",
              type: ColumnType.Text,
              width: 100,
            },
            {
              title: t("masterdata.lw_details.address"),
              key: "address",
              type: ColumnType.Text,
              width: 100,
            },
            {
              title: t("masterdata.lw_details.postal_code"),
              key: "postalCode",
              type: ColumnType.Text,
              width: 100,
            },
            {
              title: t("masterdata.lw_details.ort"),
              key: "place",
              type: ColumnType.Text,
              width: 100,
            },
          ]}
          data={data}
          defaultPageSize={10}
          rowSelection={{
            type: "radio",
            onChange: (_, selectedRows: Treuhaender[]) => {
              const selectedRow = selectedRows[0];
              setSelectedTH(selectedRow.id);
            },
          }}
          stickyHeader
          withTitle={false}
        />
      </div>
      <Divider />
      <Row gutter={[10, 10]}>
        <Col span={6}>
          <Button icon={<LeftOutlined />} onClick={onClose} type="dashed" block>
            {t("go_back")}
          </Button>
        </Col>
        <Col span={9}>
          <Button
            onClick={() => setCreateNewTHFormModalVisible(true)}
            icon={<PlusOutlined />}
            type="default"
            block
          >
            {t("masterdata.lw_details.new_contact_person")}
          </Button>
        </Col>
        <Col span={9}>
          <Button
            disabled={!selectedTH}
            onClick={showConfirmationModal}
            icon={<CheckOutlined />}
            type="primary"
            block
          >
            {t("confirm")}
          </Button>
        </Col>
        <ConfirmationModal
          visible={confirmationModalVisible}
          isLoading={
            addExistingTHResult.isLoading || addExistingTHsResult.isLoading
          }
          title={t("masterdata.lw_details.change_new_th")}
          description={t("masterdata.lw_details.change_new_th_message")}
          onClose={() => setConfirmationModalVisible(false)}
          onConfirm={onConfirm}
        />
        <CreateNewTHForm
          treuhandstelleId={selectedTHs}
          onClose={() => setCreateNewTHFormModalVisible(false)}
          onSuccess={() => {
            onInitialSearch({
              treuhandstelleId: selectedTHs,
            });
          }}
          visible={createNewTHFormModalVisible}
        />
      </Row>
    </Modal>
  );
}

export default SearchTH;
